import type { Vec2, Player, FocusAimState } from '../types.js';
import { Keyboard } from './keyboard.js';
import { Mouse, type ClientToWorld } from './mouse.js';
import { GamepadAdapter, type GamepadSample } from './gamepad.js';
import { TouchControls } from './touch.js';

/**
 * Per-tick input sample consumed by the simulation.
 *
 * - `moveDir` is a normalized vector or zero.
 * - `moveMagnitude` is in [0, 1]; keyboard input is always 1 when active.
 * - `focusAim` carries the Focus Aim hold state and aim direction.
 * - `dashPressed`, `pausePressed`, and `firePressed` are one-tick
 *   rising-edge actions.
 * - `device` reflects the dominant input device for this tick.
 */
export interface InputSample {
  moveDir: Vec2;
  moveMagnitude: number;
  focusAim: FocusAimState;
  dashPressed: boolean;
  pausePressed: boolean;
  /** Enter key: call the next wave early (skip the intermission). */
  callPressed: boolean;
  /** B key: open the intermission supply-drop shop. */
  shopPressed: boolean;
  firePressed: boolean;
  /** Direction the manual turret should fire in (pointer tap/click), or null
   * to let the sim pick the aim (gamepad / no pointer). */
  fireDir: Vec2 | null;
  device: 'keyboard' | 'mouse' | 'gamepad' | 'touch';
}

/**
 * Aggregates keyboard, mouse, touch, and gamepad input into a single per-tick
 * `InputSample`.
 *
 * Priority: gamepad > touch > mouse > keyboard.
 */
export class InputManager {
  private readonly keyboard: Keyboard;
  private readonly mouse: Mouse;
  private readonly gamepad: GamepadAdapter;
  private readonly touch: TouchControls;

  constructor(
    canvas: HTMLCanvasElement,
    clientToWorld?: ClientToWorld
  ) {
    this.keyboard = new Keyboard();
    this.mouse = new Mouse(canvas, clientToWorld);
    this.gamepad = new GamepadAdapter();
    this.touch = new TouchControls(canvas, clientToWorld);
  }

  /**
   * Build the input sample for the current tick.
   *
   * `playerPos` is used to convert the mouse cursor into a world-space aim
   * direction.
   */
  sample(playerPos: Vec2, tick: number): InputSample {
    const kb = this.keyboard.sample(tick);
    const mouseAim = this.mouse.sample(playerPos);
    const gp = this.gamepad.sample(tick);
    const tc = this.touch.sample(playerPos, tick);

    const mouseFire = this.mouse.consumeFire();
    // LMB hold steers the ship toward the cursor (pointer-follow controls);
    // a quick click still fires the turret on release.
    const mouseFollow = this.mouse.sampleFollow(performance.now());
    // Idle keyboard must report magnitude 0: a (0,0) direction at magnitude 1
    // would overwrite the movement context's lastHeading every idle tick and
    // silently break dash-from-rest on every device (mobile double-tap).
    const kbIdle = kb.move.x === 0 && kb.move.y === 0;
    const base: InputSample = {
      moveDir: mouseFollow.active ? mouseFollow.dir : kb.move,
      moveMagnitude: mouseFollow.active ? mouseFollow.magnitude : kbIdle ? 0 : 1,
      focusAim: mouseAim,
      dashPressed: kb.dashPressed,
      pausePressed: kb.pausePressed,
      callPressed: kb.callPressed,
      shopPressed: kb.shopPressed,
      firePressed: mouseFire.pressed,
      fireDir: mouseFire.dir,
      device: mouseFollow.active ? 'mouse' : 'keyboard',
    };

    // If the mouse was used this frame (cursor moved or RMB held), treat the
    // dominant device as mouse while still allowing keyboard move/dash/pause.
    if (this.mouse.wasUsedThisFrame()) {
      base.device = 'mouse';
      base.focusAim = mouseAim;
    }

    // Touch overrides keyboard+mouse when it was used this frame.
    if (this.touch.wasUsedThisFrame()) {
      base.moveDir = tc.moveDir;
      base.moveMagnitude = tc.moveMagnitude;
      base.focusAim = tc.focusAim;
      base.dashPressed = tc.dashPressed;
      base.pausePressed = tc.pausePressed;
      base.firePressed = tc.firePressed;
      base.fireDir = tc.fireDir;
      base.device = 'touch';
    }

    // Gamepad overrides everything when it is actively providing input.
    if (gp.connected && isGamepadActive(gp)) {
      base.moveDir = gp.move;
      base.moveMagnitude = gp.moveMagnitude;
      base.focusAim = gp.focusAim;
      base.dashPressed = gp.dashPressed;
      base.pausePressed = gp.pausePressed;
      base.firePressed = gp.firePressed;
      base.fireDir = null; // gamepad has no pointer; the sim picks the aim
      base.device = 'gamepad';
    }

    this.mouse.clearFrame();
    this.touch.clearFrame();
    return base;
  }

  /**
   * Show or hide the on-screen touch controls (e.g. from a settings toggle).
   */
  showTouchControls(visible: boolean): void {
    this.touch.setVisible(visible);
  }

  /**
   * Drop queued keyboard one-tick edges (dash/pause/call/shop). The shop
   * overlay calls this on close so keys pressed while the sim was paused
   * don't leak into the resumed sim. Also clears the touch adapter's edges
   * (taps made while the pause menu was up must not fire on resume).
   */
  clearPressedEdges(): void {
    this.keyboard.clearPressedEdges();
    this.touch.clearPressedEdges();
  }

  dispose(): void {
    this.keyboard.dispose();
    this.mouse.dispose();
    this.gamepad.dispose();
    this.touch.dispose();
  }
}

function isGamepadActive(gp: GamepadSample): boolean {
  return (
    gp.moveMagnitude > 0 ||
    gp.focusAim.held ||
    gp.dashPressed ||
    gp.pausePressed ||
    gp.firePressed
  );
}

/**
 * Convenience helper to build an `InputSample` directly in tests or headless
 * harnesses.
 */
export function makeInputSample(
  overrides: Partial<InputSample> = {}
): InputSample {
  return {
    moveDir: { x: 0, y: 0 },
    moveMagnitude: 0,
    focusAim: { held: false, dir: { x: 0, y: 1 } },
    dashPressed: false,
    pausePressed: false,
    callPressed: false,
    shopPressed: false,
    firePressed: false,
    fireDir: null,
    device: 'keyboard',
    ...overrides,
  };
}
